import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AssetCompanyIcon from '~/assets/asset-company-icon.svg?react';
import { AddCompanyLayout } from '~/components/@sibill-components/AutomaticallyAddSecondCompany/AddCompanyLayout';
import AddCompanySearch from '~/components/@sibill-components/AutomaticallyAddSecondCompany/AddCompanySearch';
import CheckCompany, {
  ExistingCompany,
} from '~/components/@sibill-components/AutomaticallyAddSecondCompany/CheckCompany';
import CreateCompanyForm, {
  CompanyData,
} from '~/components/@sibill-components/AutomaticallyAddSecondCompany/CreateCompanyForm';
import JoinCompany from '~/components/@sibill-components/AutomaticallyAddSecondCompany/JoinCompany';
import SubscribeCompany from '~/components/@sibill-components/AutomaticallyAddSecondCompany/SubscribeCompany';
import IconEdit from '~/icons/icon-edit.svg?react';
import IconPadlockClose from '~/icons/icon-padlock-closed.svg?react';
import IconReviewFill from '~/icons/icon-review-fill.svg?react';
import IconStore from '~/icons/icon-store.svg?react';
import { ModalProps } from '~/modals/Modal';
import { useFetchNewSecondCompanySubscriptionInfo } from '~/services/subscription';
import { NewSecondCompanySubscriptionInfoResponse } from '~/types/subscriptions';
import { isAxiosError } from '~/utils/helpers';

enum AddCompanyPhase {
  Identify,
  Join,
  CheckCompany,
  Create,
  Subscribe,
}

type AddCompanyState =
  | {
      phase: AddCompanyPhase.Identify;
      taxNumber: string;
    }
  | {
      phase: AddCompanyPhase.Join;
      taxNumber: string;
    }
  | {
      phase: AddCompanyPhase.CheckCompany;
      company: ExistingCompany;
    }
  | {
      phase: AddCompanyPhase.Create;
      taxNumber: string;
    }
  | {
      phase: AddCompanyPhase.Subscribe;
      message: {
        companyData: ExistingCompany | CompanyData;
        subscriptionInfo?: NewSecondCompanySubscriptionInfoResponse;
      };
    };

export const AddCompanyModal = (props: ModalProps) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company' });
  const [state, setState] = useState<AddCompanyState>({
    phase: AddCompanyPhase.Identify,
    taxNumber: '',
  });
  const { mutateAsync: fetchNewSecondCompanySubscriptionInfo, isPending } = useFetchNewSecondCompanySubscriptionInfo();

  switch (state.phase) {
    case AddCompanyPhase.Identify:
      return (
        <AddCompanyLayout title={t('steps.search_company.title')} titleIcon={IconStore} step="search">
          <AddCompanySearch
            closeModal={props.closeModal}
            initialValue={state.taxNumber}
            onNotFound={(taxNumber) => setState({ phase: AddCompanyPhase.Create, taxNumber })}
            onFound={(taxNumber, companyIdentity, available) => {
              const province = companyIdentity.provinceCode !== null ? `(${companyIdentity.provinceCode}` : '';
              const address = `${[companyIdentity.address, companyIdentity.postalCode, companyIdentity.city]
                .filter((s) => s !== null)
                .join(' - ')} ${province}`;
              const companyData: ExistingCompany = {
                name: companyIdentity.companyName || '',
                vatNumber: companyIdentity.vatNumber || '',
                taxNumber: companyIdentity.taxNumber,
                address,
                companyIdentityId: companyIdentity.id,
              };
              if (available) {
                setState({ phase: AddCompanyPhase.CheckCompany, company: companyData });
              } else {
                setState({ phase: AddCompanyPhase.Join, taxNumber });
              }
            }}
          />
        </AddCompanyLayout>
      );
    case AddCompanyPhase.Create:
      return (
        <AddCompanyLayout title={t('steps.create_company.title')} titleIcon={IconEdit} step="company-data">
          <CreateCompanyForm
            initialValues={{
              country: 'IT',
              taxNumber: state.taxNumber || '',
            }}
            vatNumberDisabled={false}
            onSubmit={async (company) => {
              try {
                const subscriptionInfo = await fetchNewSecondCompanySubscriptionInfo();
                setState({
                  phase: AddCompanyPhase.Subscribe,
                  message: { companyData: company, subscriptionInfo },
                });
              } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                  setState({ phase: AddCompanyPhase.Subscribe, message: { companyData: company } });
                }
              }
            }}
            closeModal={props.closeModal}
            isLoading={isPending}
          />
        </AddCompanyLayout>
      );
    case AddCompanyPhase.Join:
      return (
        <AddCompanyLayout title={t('steps.join_company.title')} titleIcon={IconPadlockClose} step="complete">
          <JoinCompany closeModal={props.closeModal} taxNumber={state.taxNumber} />
        </AddCompanyLayout>
      );
    case AddCompanyPhase.CheckCompany:
      return (
        <AddCompanyLayout title={t('steps.check_company.title')} titleIcon={IconReviewFill} step="company-data">
          <CheckCompany
            closeModal={props.closeModal}
            onSearch={() => {
              setState({ phase: AddCompanyPhase.Identify, taxNumber: '' });
            }}
            companyData={state.company}
            onNext={async (company) => {
              try {
                const subscriptionInfo = await fetchNewSecondCompanySubscriptionInfo();
                setState({
                  phase: AddCompanyPhase.Subscribe,
                  message: { companyData: company, subscriptionInfo },
                });
              } catch (error) {
                if (isAxiosError(error) && error.response?.status === 404) {
                  setState({ phase: AddCompanyPhase.Subscribe, message: { companyData: company } });
                }
              }
            }}
            isLoading={isPending}
          />
        </AddCompanyLayout>
      );

    case AddCompanyPhase.Subscribe:
      return (
        <AddCompanyLayout
          title={
            state.message.subscriptionInfo ? t('steps.subscribe.title_activate') : t('steps.subscribe.title_request')
          }
          titleIcon={AssetCompanyIcon}
          step="complete"
        >
          <SubscribeCompany
            closeModal={props.closeModal}
            companyData={state.message.companyData}
            subscriptionInfo={state.message.subscriptionInfo}
          />
        </AddCompanyLayout>
      );
  }
};
